import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import states from 'states-us';
import * as Icons from 'react-icons/fa';

import { Container, HeaderText, Spinner, Button, DropDown, StatusPill, PopUpConfirmation } from '../../../components';
import { NotificationContext } from '../../../helpers/AlertContext/AlertContext.js';
import {
  approveUser,
  enableUser,
  disableUser,
  updateUserType,
  deleteUser,
  denyUser,
} from '../../../generated/graphql/mutations';
import { getUserById } from '../../../generated/graphql/queries';
import { VolunteerStatus, UserTypes, EmployeeType } from '../../../constants';
import { useUser } from '../../../contexts/userContext';
import { Link } from 'react-router-dom';
import UsersAwardedLevels from '../../../components/UsersAwardedLevels';
import { useAwardLevels } from '../../../helpers/useAwardLevels.js';
import { parseMarkdown } from '../../../helpers/markdown/parser.js';

const denyMessage =
  "We are unable to approve your certificate you've uploaded. This is an incorrect certificate for access to Project 1591. The only approved certificate is the one you receive at the end of the PURSUIT Training & Assessment course that you were directed to when signing up for Project 1591. We have reset your status to UNCERTIFIED and removed the certificate you uploaded. Please upload the correct one when you receive it. Thanks. If you have questions, feel free to contact us at [project1591@guardiangroup.org](mailto:project1591@guardiangroup.org). Good luck.";

const UserDetails = () => {
  const { user: signedInUser, isAdmin } = useUser();
  const [, setAlert] = useContext(NotificationContext);

  const { userId } = useParams();

  const [user, setUser] = useState();
  const [userType, setUserType] = useState({ label: 'Volunteer', value: UserTypes.Volunteer });

  const [loading, setLoading] = useState();
  const [approving, setApproving] = useState(false);
  const [denying, setDenying] = useState(false);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);
  const [showConfirmEnable, setShowConfirmEnable] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmDeny, setShowConfirmDeny] = useState(false);
  const [sendDenyEmail, setSendDenyEmail] = useState(true);
  const [disabling, setDisabling] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [changingType, setChangingType] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [disabledReason, setDisabledReason] = useState(null);
  const [deletedReason, setDeletedReason] = useState(null);

  const userTypeOptions = [
    { label: 'Admin', value: UserTypes.Admin },
    { label: 'Staff', value: UserTypes.Staff },
    { label: 'Volunteer', value: UserTypes.Volunteer },
    { label: 'LEO', value: UserTypes.LEO },
  ];

  const { awardLevels } = useAwardLevels();

  useEffect(() => {
    const getUser = async () => {
      try {
        setLoading(true);

        const {
          data: { getUserById: returnedUser = null },
        } = await API.graphql(
          graphqlOperation(getUserById, {
            id: userId,
          })
        );

        if (!returnedUser) {
          setAlert({
            type: 'SET_NOTIFICATION',
            payload: {
              occurs: true,
              message: 'User not found',
              textColor: 'redText',
              borderColor: 'redBorder',
            },
          });
        }

        setUser(returnedUser);
        setUserType(getUserType(returnedUser.userStatus));
      } catch (error) {
        console.error('Error fetching user by id:', error);

        setAlert({
          type: 'SET_NOTIFICATION',
          payload: {
            occurs: true,
            message: 'Error fetching user details',
            textColor: 'redText',
            borderColor: 'redBorder',
          },
        });

        setUser(null);
      }

      setLoading(false);
    };

    getUser(userId);
  }, [userId, setAlert]);

  const parseState = stateCode => {
    if (!stateCode) {
      return '-';
    }

    const matchingState = states.find(x => x.abbreviation === stateCode);

    if (matchingState) {
      return matchingState.name;
    }

    return stateCode;
  };

  const parseDate = date => {
    if (!date) {
      return '-';
    }

    const luxonDate = DateTime.fromISO(date);

    if (!luxonDate.isValid) {
      return 'Invalid Date';
    }

    return luxonDate.toLocaleString();
  };

  const getCertificate = async () => {
    const key = user.certificatePath;
    const { identityId } = user;

    const fileUrl = await Storage.get(key, { level: 'protected', identityId });

    window.open(fileUrl, '_blank');
  };

  const onApproveUser = async () => {
    try {
      setApproving(true);

      const {
        data: { approveUser: returnedUser = null },
      } = await API.graphql(
        graphqlOperation(approveUser, {
          id: userId,
        })
      );

      if (!returnedUser) {
        throw new Error('No user returned');
      }

      toast.success('User Approved', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setUser(returnedUser);
    } catch (error) {
      console.error('Error approving user:', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error approving user',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setApproving(false);
  };

  const onDenyUser = async () => {
    try {
      setDenying(true);
      setShowConfirmDeny(false);

      const {
        data: { denyUser: returnedUser = null },
      } = await API.graphql(
        graphqlOperation(denyUser, {
          id: userId,
          sendEmail: sendDenyEmail,
          deny: true,
        })
      );

      if (!returnedUser) {
        throw new Error('No user returned');
      }

      toast.success('User Denied', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setUser(returnedUser);
    } catch (error) {
      console.error('Error denying user:', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error denying user',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setDenying(false);
  };

  const onEnableUser = async () => {
    try {
      setEnabling(true);

      const {
        data: { enableUser: returnedUser = null },
      } = await API.graphql(
        graphqlOperation(enableUser, {
          id: userId,
        })
      );

      if (!returnedUser) {
        throw new Error('No user returned');
      }

      toast.success('Enabled User', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setUser(returnedUser);
      setShowConfirmEnable(false);
    } catch (error) {
      console.error('Error enabling user:', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error enabling user',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setEnabling(false);
  };

  const onDisableUser = useCallback(async () => {
    try {
      setDisabling(true);

      const {
        data: { disableUser: returnedUser = null },
      } = await API.graphql(
        graphqlOperation(disableUser, {
          id: userId,
          disableReason: disabledReason,
        })
      );

      if (!returnedUser) {
        throw new Error('No user returned');
      }

      toast.success('Disabled User', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setUser(returnedUser);
      setShowConfirmDisable(false);
    } catch (error) {
      console.error('Error disabling user:', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error disabling user',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setDisabling(false);
  }, [userId, disabledReason]);

  const onDeleteUser = useCallback(async () => {
    try {
      setIsDeleting(true);

      const {
        data: { deleteUser: returnedUser = null },
      } = await API.graphql(
        graphqlOperation(deleteUser, {
          id: userId,
          deletedReason: deletedReason,
        })
      );

      if (!returnedUser) {
        throw new Error('No user returned');
      }

      toast.success('Deleted User', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setUser(returnedUser);
      setShowConfirmDelete(false);
    } catch (error) {
      console.error('Error deleting user:', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error deleting user',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setIsDeleting(false);
  }, [userId, deletedReason]);

  const getUserType = userStatus => {
    let type = null;

    if (userStatus === EmployeeType.Admin || userStatus === EmployeeType.Staff) {
      type = userTypeOptions.find(x => x.value === userStatus);
    } else {
      type = userTypeOptions.find(x => x.value === UserTypes.Volunteer);
    }

    return type;
  };

  const onUpdateUserType = async () => {
    const selectedType = userType?.value;

    try {
      setChangingType(true);

      if (user?.userStatus === 'DISABLED') {
        setAlert({
          type: 'SET_NOTIFICATION',
          payload: {
            occurs: true,
            message: "Error - Can't change user type of a disabled user",
            textColor: 'redText',
            borderColor: 'redBorder',
          },
        });

        setChangingType(false);

        return;
      }

      const {
        data: { updateUserType: returnedUser = null },
      } = await API.graphql(
        graphqlOperation(updateUserType, {
          id: userId,
          type: selectedType,
        })
      );

      if (!returnedUser) {
        throw new Error('No user returned');
      }

      toast.success('User Type Updated', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setUser(returnedUser);
    } catch (error) {
      console.error('Error updating user type:', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error updating user type',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setChangingType(false);
  };

  // const redirectToMessageUser = useCallback(() => {
  //   history.push(`/messages/create?userIds=${userId}`);
  // }, [history, userId]);

  const disabledReasonInput = (
    <textarea
      value={disabledReason}
      onChange={e => setDisabledReason(e.target.value)}
      placeholder="Reason for disabling"
      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-guardian-blue"
    />
  );

  const disabledContent = (
    <div className="flex flex-col">
      <p className="text-pursuit-gray mb-8">
        Are you sure you want to disable {user?.firstName && user.firstName} {user?.lastName && user.lastName}
        {!user?.firstName && !user?.lastName ? 'this user' : ''}?
      </p>
      {disabledReasonInput}
    </div>
  );

  const deleteReasonInput = (
    <textarea
      value={deletedReason}
      onChange={e => setDeletedReason(e.target.value)}
      placeholder="Reason for deleting"
      className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-guardian-blue"
    />
  );

  const deleteContent = (
    <div className="flex flex-col">
      <p className="text-pursuit-gray mb-8">
        Are you sure you want to delete {user?.firstName && user.firstName} {user?.lastName && user.lastName}
        {!user?.firstName && !user?.lastName ? 'this user' : ''}?
      </p>
      {deleteReasonInput}
    </div>
  );

  return (
    <>
      {showConfirmDisable && (
        <PopUpConfirmation
          title="Disable User"
          content={disabledContent}
          // content={
          //   `Are you sure you want to disable ${user?.firstName && user.firstName} ${user?.lastName && user.lastName}${
          //     !user?.firstName && !user?.lastName ? 'this user' : ''
          //   }?` + disabledReasonInput
          // }
          onCancel={() => setShowConfirmDisable(false)}
          onConfirm={() => onDisableUser()}
          confirmText="DISABLE"
          confirmLoading={disabling}
          confirmLoadingText="DISABLING"
          confirmDisabled={!disabledReason}
          destructive
          className="w-11/12 lg:w-auto"
        />
      )}

      {showConfirmDelete && (
        <PopUpConfirmation
          title="Delete User"
          content={deleteContent}
          onCancel={() => setShowConfirmDelete(false)}
          onConfirm={() => onDeleteUser()}
          confirmText="DELETE"
          confirmLoading={isDeleting}
          confirmLoadingText="DELETING"
          destructive
          className="w-11/12 lg:w-auto"
        />
      )}

      {showConfirmEnable && (
        <PopUpConfirmation
          title="Enable User"
          content={`Are you sure you want to re-enable ${user?.firstName && user.firstName} ${user?.lastName &&
            user.lastName}${!user?.firstName && !user?.lastName ? 'this user' : ''}?`}
          onCancel={() => setShowConfirmEnable(false)}
          onConfirm={() => onEnableUser()}
          confirmText="ENABLE"
          confirmLoading={enabling}
          confirmLoadingText="ENABLING"
          className="w-11/12 lg:w-auto"
        />
      )}

      {showConfirmDeny && (
        <PopUpConfirmation
          title="Deny User"
          content={
            <div>
              <p>
                Are you sure you want to deny {user?.firstName && user.firstName} {user?.lastName && user.lastName}
                {!user?.firstName && !user?.lastName ? 'this user' : ''}?
              </p>
              <div>
                <label className="inline-flex items-center mt-4">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-guardian-blue"
                    checked={sendDenyEmail}
                    onChange={() => setSendDenyEmail(!sendDenyEmail)}
                  />
                  <span className="ml-2">Send denial email?</span>
                </label>
              </div>
              {sendDenyEmail && (
                <div
                  className="border p-4 mt-4 rounded"
                  dangerouslySetInnerHTML={{
                    __html: parseMarkdown(denyMessage),
                  }}
                />
              )}
            </div>
          }
          onCancel={() => setShowConfirmDeny(false)}
          onConfirm={() => onDenyUser()}
          confirmText={sendDenyEmail ? 'CONFIRM & SEND' : 'CONFIRM'}
          confirmLoading={denying}
          confirmLoadingText="DENYING"
          destructive
          className="w-11/12 lg:w-auto"
        />
      )}

      <Container
        height="lg:min-h-104"
        width="lg:w-4/5"
        padding="p-4 md:px-10 md:py-8 lg:px-20"
        className="flex flex-col"
        margin="mx-3 md:mx-12 lg:mx-auto mt-4">
        <HeaderText fontSize="text-4xl" className="mb-12">
          User Details
        </HeaderText>

        {loading ? (
          <div className="w-full flex justify-center items-center">
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="flex flex-col-reverse lg:flex-row">
              <div className="w-full lg:mr-4 lg:border-r border-gray-200">
                <div className="flex flex-col">
                  <div className="flex flex-row w-full">
                    <div className="flex flex-col w-1/2">
                      <p className="light-primary-blue-text font-semibold mb-1 mr-1">First Name</p>
                      <p className="text-pursuit-gray mb-8">{user?.firstName || '-'}</p>
                    </div>

                    <div className="flex flex-col w-1/2">
                      <p className="light-primary-blue-text font-semibold mb-1 mr-1">Last Name</p>
                      <p className="text-pursuit-gray mb-8">{user?.lastName || '-'}</p>
                    </div>
                  </div>

                  <p className="light-primary-blue-text font-semibold mb-1 mr-1">Country</p>
                  <p className="text-pursuit-gray mb-8">{user?.country || '-'}</p>

                  {user?.country === 'United States' && (
                    <div className="flex flex-row">
                      <div className="flex flex-col w-1/2">
                        <p className="light-primary-blue-text font-semibold mb-1 mr-1">State</p>
                        <p className="text-pursuit-gray mb-8">{parseState(user?.state)}</p>
                      </div>

                      <div className="flex flex-col w-1/2">
                        <p className="light-primary-blue-text font-semibold mb-1 mr-1">Zip</p>
                        <p className="text-pursuit-gray mb-8">{user?.zip || '-'}</p>
                      </div>
                    </div>
                  )}

                  <p className="light-primary-blue-text font-semibold mb-1 mr-1">Heard About Us</p>
                  <p className="text-pursuit-gray mb-8">{user?.heardAboutUs || '-'}</p>

                  <p className="light-primary-blue-text font-semibold mb-1 mr-1">Date of Birth</p>
                  <p className="text-pursuit-gray mb-8">{user?.dateOfBirth || '-'}</p>
                </div>
              </div>

              <div className="w-full lg:pl-16">
                <div className="flex flex-row">
                  <div className="flex flex-col w-1/2">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Alias</p>
                    <p className="text-pursuit-gray mb-8">{user?.alias || '-'}</p>
                  </div>

                  <div className="flex flex-col w-1/2">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Status</p>
                    <StatusPill status={user?.userStatus} />
                  </div>
                </div>

                <p className="light-primary-blue-text font-semibold mb-1 mr-1">Email</p>
                <div className="mb-8">
                  {user?.userStatus === VolunteerStatus.Certified ? (
                    <Link to={`/messaging/create?recipients=${userId}`} className="text-guardian-blue">
                      {/* <Button
                        linedBlue
                        noPadding
                        className="px-4 w-full lg:w-auto uppercase"
                        // onClick={() => setShowNotificationModal(true)}
                      > */}
                      {user?.email || '-'}
                      {/* </Button> */}
                    </Link>
                  ) : (
                    <a
                      href={`mailto:${user?.email}?cc=project1591@guardiangroup.org`}
                      className="text-guardian-blue"
                      target="_blank"
                      rel="noopener noreferrer">
                      {user?.email || '-'}
                    </a>
                  )}
                </div>

                <div className="flex flex-row">
                  <div className="flex flex-col w-1/3">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Created</p>
                    <p className="text-pursuit-gray mb-8">{parseDate(user?.createdAt)}</p>
                  </div>

                  <div className="flex flex-col w-1/3">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Approved</p>
                    <p className="text-pursuit-gray mb-8">{parseDate(user?.approvedAt)}</p>
                  </div>

                  <div className="flex flex-col w-1/3">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Last Sign In</p>
                    <p className="text-pursuit-gray mb-8">{parseDate(user?.lastSignInAt)}</p>
                  </div>
                </div>

                {user?.scoreAwardLevels && (
                  // <div className="flex flex-col">
                  //   <p className="light-primary-blue-text font-semibold mb-1 mr-1">Score Award Levels</p>
                  //   <p className="text-pursuit-gray mb-8">{JSON.stringify(user?.scoreAwardLevels) || '-'}</p>
                  // </div>
                  <UsersAwardedLevels usersAwardLevels={user.scoreAwardLevels} possibleAwardLevels={awardLevels} />
                )}

                {user?.certificatePath && (
                  <div className="mb-8">
                    <Button
                      onClick={() => getCertificate()}
                      className="text-guardian-blue underline focus:outline-none">
                      View Certificate
                    </Button>
                  </div>
                )}

                {(user?.userStatus === VolunteerStatus.Disabled || user?.userStatus === VolunteerStatus.Deleted) && (
                  <div className="flex flex-col">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Disabled Reason</p>
                    <p className="text-pursuit-gray mb-8">{user?.disabledReason || '-'}</p>
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Disabled at</p>
                    <p className="text-pursuit-gray mb-8"> {user?.disabledAt || '-'} </p>
                  </div>
                )}

                {user?.userStatus === VolunteerStatus.Deleted && (
                  <div className="flex flex-col">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Deleted Reason</p>
                    <p className="text-pursuit-gray mb-8">{user?.deletedReason || '-'}</p>
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">Deleted at</p>
                    <p className="text-pursuit-gray mb-8"> {user?.deletedAt || '-'} </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:justify-between">
              {isAdmin && user?.id !== signedInUser?.id && (
                <div className="flex flew-row items-end">
                  <div className="flex flex-col w-full lg:w-auto">
                    <p className="light-primary-blue-text font-semibold mb-1 mr-1">User Type</p>
                    <DropDown
                      value={userType}
                      onChange={option => setUserType(option)}
                      width="w-full lg:w-64"
                      containerClassName="mr-2"
                      options={userTypeOptions}
                    />
                  </div>

                  <Button solidBlue noPadding className="px-4 ml-6" onClick={onUpdateUserType} loading={changingType}>
                    {changingType ? 'SAVING' : 'SAVE'}
                  </Button>
                </div>
              )}

              {user?.id !== signedInUser?.id && (
                <div className="flex justify-end mt-8 w-full lg:w-auto">
                  {/* User notification button */}
                  {/* {user?.userStatus !== VolunteerStatus.Deleted ? ( */}
                  {user?.userStatus === VolunteerStatus.Certified ? (
                    <Link to={`/messaging/create?recipients=${userId}`}>
                      <Button
                        linedBlue
                        noPadding
                        className="px-4 w-full lg:w-auto uppercase"
                        // onClick={() => setShowNotificationModal(true)}
                      >
                        Message
                      </Button>
                    </Link>
                  ) : null}

                  {user?.userStatus === VolunteerStatus.Uncertfied ||
                  user?.userStatus === VolunteerStatus.Disabled ||
                  user?.userStatus === VolunteerStatus.Pending ? (
                    <a
                      href={`mailto:${user.email}?cc=project1591@guardiangroup.org`}
                      target="_blank"
                      rel="noopener noreferrer">
                      <Button linedBlue noPadding className="px-4 w-full lg:w-auto uppercase">
                        Email
                      </Button>
                    </a>
                  ) : null}

                  {user?.userStatus !== VolunteerStatus.Deleted ? (
                    user?.userStatus !== VolunteerStatus.Disabled ? (
                      <Button
                        linedRed
                        noPadding
                        className="px-4 lg:ml-6 w-full lg:w-auto"
                        onClick={() => setShowConfirmDisable(true)}>
                        DISABLE
                      </Button>
                    ) : (
                      <Button
                        linedBlue
                        noPadding
                        className="px-4 lg:ml-6 w-full lg:w-auto"
                        onClick={() => setShowConfirmEnable(true)}>
                        ENABLE
                      </Button>
                    )
                  ) : null}
                  {user?.userStatus === VolunteerStatus.Disabled ? (
                    <Button
                      linedRed
                      noPadding
                      className="px-4 lg:ml-6 w-full lg:w-auto"
                      onClick={() => setShowConfirmDelete(true)}>
                      DELETE
                    </Button>
                  ) : null}

                  {user?.userStatus === VolunteerStatus.Pending && (
                    <Button
                      solidBlue
                      noPadding
                      className="px-4 lg:ml-6 w-full lg:w-auto"
                      onClick={onApproveUser}
                      loading={approving}>
                      {approving ? 'APPROVING' : 'APPROVE'}
                    </Button>
                  )}

                  {user?.userStatus === VolunteerStatus.Pending && (
                    <Button
                      solidRed
                      noPadding
                      className="px-4 lg:ml-6 w-full lg:w-auto"
                      onClick={() => setShowConfirmDeny(true)}
                      loading={denying}>
                      {denying ? 'DENYING' : 'DENY'}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

export default UserDetails;
